/**
 * hydration.js — buildInitialValues + KNOWN_SKIP dev guardrail.
 *
 * Extracted from useBookingForm.js in refactor R-2 (2026-07-14).
 * Refactored in R-7 (2026-07-14) to derive its field-by-field
 * hydration from `/lib/booking-fields.js` (single source of truth
 * shared with `bookingDefaults()` and `buildBookingPayload()`).
 * Only fields that CAN'T live in the static Map are still hydrated
 * inline below:
 *   • booking_date / start_time / end_time / _booking_tz — composed
 *     from scheduled_start/end via decomposeInSiteTz
 *   • site_rules_accepted — form-only, doesn't round-trip through
 *     the Map because it isn't a DB column read by the form
 */
import { decomposeInSiteTz } from "@/lib/datetime";
import { bookingDefaults } from "@/lib/constants";
import { MAP_COVERED_DB_COLUMNS, deriveHydration } from "@/lib/booking-fields";
import { answersFromSnapshot } from "@/lib/api/declarations";

export function buildInitialValues({ booking, defaultSiteId, defaultBayId, prefill }) {
  const base = bookingDefaults();
  if (defaultSiteId) base.site_id = defaultSiteId;
  if (defaultBayId)  base.bay_id  = defaultBayId;

  if (prefill) {
    if (prefill.booking_date) base.booking_date = prefill.booking_date;
    if (prefill.start_time)   base.start_time   = prefill.start_time;
    if (prefill.end_time)     base.end_time     = prefill.end_time;
    const _tz = prefill.timezone || "Australia/Sydney";
    if (prefill.scheduledStart) {
      const p = decomposeInSiteTz(prefill.scheduledStart, _tz);
      base.booking_date = p.dateStr;
      base.start_time   = p.timeStr;
    }
    if (prefill.scheduledEnd) {
      const p = decomposeInSiteTz(prefill.scheduledEnd, _tz);
      base.end_time = p.timeStr;
    }
    base._booking_tz = _tz;
    const map = {
      title: "title", customer_reference: "title",
      sender_name: "sender_name", sender_company: "sender_company",
      receiver_name: "receiver_name", receiver_company: "receiver_company",
      po_number: "po_number", manifest_number: "manifest_number",
      goods_description: "goods_description",
      pallet_type: "pallet_type", pallets_in: "pallets_in", pallets_out: "pallets_out",
      vehicle_type: "vehicle_type",
      approved_booker_id: "approved_booker_id",
    };
    for (const [from, to] of Object.entries(map)) {
      if (prefill[from] !== undefined && prefill[from] !== null && prefill[from] !== "" && !base[to]) {
        base[to] = prefill[from];
      }
    }
    if (prefill.dangerous_goods) base.dangerous_goods = ["dangerous_goods"];
  }

  if (!booking) return base;

  // ── Composed schedule fields (can't live in the Map — they read
  //    scheduled_start/end and split into three form fields).
  // Always in the SITE's timezone (never the viewer's browser / UTC).
  const tz = booking.site?.timezone || null;
  const s = booking.scheduled_start && tz ? decomposeInSiteTz(booking.scheduled_start, tz) : null;
  const e = booking.scheduled_end   && tz ? decomposeInSiteTz(booking.scheduled_end, tz)   : null;

  const hydrated = {
    ...base,

    // Everything else (root columns + vehicle_regos / mass_limits /
    // load_restraint / trailer_weights JSONB children) hydrates via
    // the single source-of-truth Map.
    ...deriveHydration(booking),

    // Composed schedule fields
    booking_date: s ? s.dateStr : base.booking_date,
    start_time:   s ? s.timeStr : base.start_time,
    end_time:     e ? e.timeStr : base.end_time,
    _booking_tz:  tz || base._booking_tz,
    _tz_hydrated: !!tz,

    // Form-only gate for the Confirm-and-save button. NOT covered by
    // the Map because it isn't in ROOT_FIELDS (never written to DB
    // by the form). Hydrated here so an existing booking that has
    // already accepted site rules doesn't re-prompt on edit.
    site_rules_accepted: !!booking.site_rules_accepted,

    // Driver declarations: snapshot JSONB → {id: bool}; saved via RPC, not the Map.
    declaration_answers: answersFromSnapshot(booking.declarations),
  };

  // ── Dev-only: warn about DB columns we fetched but never spread into
  //    `values`. Guards against silent field-drop bugs when a new column
  //    is added to lbs_bookings but this hydration map isn't updated
  //    (this is exactly what caused BK260703-FE1F5F to lose driver_tba).
  //    Anything covered by the source-of-truth Map or explicitly
  //    listed in KNOWN_SKIP is fine.
  if (process.env.NODE_ENV !== "production") {
    const KNOWN_SKIP = new Set([
      // identity / audit / lifecycle — surfaced elsewhere
      "id", "reference_number", "status", "created_at", "updated_at", "declarations",
      "created_by_user_id", "created_by_role", "created_by_email",
      "client_id", "user_id", "rejection_reason",
      // scheduled_* → decomposed into booking_date / start_time / end_time
      "scheduled_start", "scheduled_end",
      // driver denorm (rendered via slots, not editable here)
      "driver_name", "driver_phone", "driver_email",
      // arrival / check-in surfaced via BookingStatusActions
      "arrival_time", "arrival_capture_method", "checked_in_at", "checked_in_by",
      "completed_at", "cancelled_at", "no_show_at",
      // recurring & public link plumbing (not edited via form)
      "recurring_pattern_id", "recurring_instance_of", "public_link_id",
      // doc-ai plumbing (dedicated Docs tab renders separately)
      "doc_ai_parse", "doc_ai_status", "doc_ai_attachments_at",
      // booker-update workflow (rendered inline via banner)
      "booker_update_requested_at", "booker_update_note",
      // NCR / sampling / lifecycle telemetry (view-only)
      "ncr_flags", "sampling_bucket", "sampling_reason",
      // Audit / ownership (populated by DB triggers or backend)
      "owner_user_id", "approved_by", "approved_at",
      "hold_set_by_user_id", "hold_set_at",
      "qa_confirmed_at", "qa_confirmed_by_user_id",
      // Lifecycle timestamps (set by check-in/out & signature flows)
      "checkin_time", "checkout_time", "arrival_eta_at",
      "site_rules_accepted_at", "driver_signed_name",
      // Public-link / recurring plumbing (creator only)
      "is_public_booking", "is_recurring", "public_booking_link_id",
      "rebooked_from_id", "driver_site_info_token",
      // Legacy / historical columns superseded by typed columns
      // (kept in schema, no longer written by the form)
      "load_restraint_rule_text", "restraint_count", "restraint_details",
      "internal_notes",
    ]);
    const dropped = Object.keys(booking || {}).filter(
      (k) =>
        !MAP_COVERED_DB_COLUMNS.has(k) &&
        !KNOWN_SKIP.has(k) &&
        !k.startsWith("_") &&
        // site_rules_accepted is hydrated inline above but not in the Map
        k !== "site_rules_accepted"
    );
    if (dropped.length > 0) {
      console.warn(
        "[useBookingForm] booking columns fetched but NOT hydrated into " +
        "`values` — any edit will silently write empty/default values back " +
        "to Postgres. Add them to /lib/booking-fields.js ROOT_FIELDS " +
        "(preferred — auto-updates defaults + payload + hydration) or " +
        "to KNOWN_SKIP in this file (for columns that shouldn't round-trip):",
        dropped
      );
    }
  }

  return hydrated;
}
